import { AgNumberValueCellEditingRenderer } from 'components/AgGrid/AgNumberValueCellEditingRenderer';
import { useIntersection } from 'hooks/useIntersection';
import type { IContractOtherCostLimitSpanSelectionItem } from 'pages/Project/UerProject/Contract/hooks/useUerContractOtherCostLimitSpanSelectionList';
import { replacerCost } from 'utils/replacerCost';

import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Style from './CostCellRendererComponent.module.css';

interface ICostCellRendererComponent extends ICellRendererParams<IContractOtherCostLimitSpanSelectionItem> {
    checkedIds: string[];
    onChange?: (id: string, value: number | null, parentKey?: string) => void;
}

export const CostCellRendererComponent = (props: ICostCellRendererComponent) => {
    const { checkedIds, onChange, data, api, value, ...rest } = props;

    const [isInView, setIsInView] = useState(true);

    const isNotChecked = useMemo(() => {
        if (data?.isGroupMember) {
            return !checkedIds.includes(data?.groupKey as string);
        }

        return !checkedIds.includes(data?.id as string);
    }, [checkedIds, data?.groupKey, data?.id, data?.isGroupMember]);

    const status = useMemo(
        () => (!isNotChecked && value && replacerCost(value) > data?.reminder ? 'alert' : undefined),
        [data?.reminder, isNotChecked, value],
    );

    const tooltipMessage = useMemo(
        () => (status === 'alert' ? 'Введенное значение не должно превышать остаток по договору' : undefined),
        [status],
    );

    const onChangeLocal = useCallback(
        (valueLocal: number | null) => {
            if (data?.id) {
                onChange?.(data?.id, valueLocal, data?.isGroupMember ? data?.groupKey : undefined);
            }
        },
        [data?.groupKey, data?.id, data?.isGroupMember, onChange],
    );

    useEffect(() => {
        if (isNotChecked && data?.id) {
            onChange?.(data.id, null, data?.isGroupMember ? data?.groupKey : undefined);
            const rowNode = api.getRowNode(data.id);

            if (rowNode) {
                rowNode.setDataValue('cost', null);
            }
        }
    }, [api, data?.groupKey, data?.id, data?.isGroupMember, isNotChecked, onChange]);

    const onLeave = useCallback(() => setIsInView(false), [setIsInView]);

    const onIntersection = useCallback(() => setIsInView(true), [setIsInView]);

    const { refCallback } = useIntersection({
        onLeave,
        onIntersection,
        isProcessing: !tooltipMessage,
    });

    return (
        <div className={Style.costCellRendererComponent}>
            <div ref={refCallback} />
            <AgNumberValueCellEditingRenderer
                data={data}
                decimalScale={2}
                idKey="id"
                columnKey="cost"
                onChange={onChangeLocal}
                disabled={isNotChecked || data?.isGroup}
                api={api}
                status={status}
                tooltipStatus={status}
                tooltipMessage={isInView ? tooltipMessage : undefined}
                tooltipDirection="rightCenter"
                isEditable={!data?.isTotal}
                defaultIfEmpty="0,00"
                value={value}
                {...rest}
            />
        </div>
    );
};

CostCellRendererComponent.displayName = 'CostCellRendererComponent';
